import { Col, Row } from 'antd'
import _ from 'lodash'

import Collapse from 'shared/components/Collapse'
import { EvictionsType } from 'shared/components/CreditAndBackgroundCheck/interfaces'
import {
  CriminalRecordsPeriod,
  CriminalRecordsTitleContainer,
  EvictionStyledCol,
  EvictionStyledLabel,
} from 'shared/components/CreditAndBackgroundCheck/styled'
import Spacer from 'shared/components/Spacer'
import Tile from 'shared/components/Tile'
import { SmallText } from 'shared/components/Typography'
import { isEvictionReportPending } from '../utils/reportStatusHelpers'
import PendingStatusCard from './PendingStatusCard'
import { BACKGROUND_CREDIT_CHECK_PROVIDER } from 'manager/pages/ExperianAuth/interfaces'
import type { Application } from 'renter/interfaces/api/application'

import { printDollarsFromCents } from 'shared/utils/dollar-print'
import { calcTileInnerBorderBottom, formatDate, getValueOrNA } from 'shared/utils/ui'

type EvictionsProps = {
  isEvictions?: boolean
  evictions?: EvictionsType
  application?: Application
}

const Evictions = ({ isEvictions, evictions, application }: EvictionsProps) => {
  const evictionsByState = evictions?.evictionRecordGroup || []
  const isExperian = application?.bccProvider === BACKGROUND_CREDIT_CHECK_PROVIDER.EXPERIAN_CONSUMER_CONNECT

  // Compute visibility + note text for MA eviction action type
  const hasMAActionType = evictionsByState.some(
    (g) => g.state === 'MA' && g.records?.some((r) => !!r.evictionActionType)
  )

  // Prefer BE note if present (top-level or group-level), fallback to static copy:
  const beNoteTop = _.get(evictions, 'evictionActionTypeNote')
  const beNoteGroup = _.get(
    evictionsByState.find((g) => g.state === 'MA'),
    'evictionActionTypeNote'
  )

  const evictionActionTypeNote = hasMAActionType
    ? (beNoteTop || beNoteGroup ||
       'Eviction Action Type means a designation of cause (fault) or no cause (no fault) at the time of filing.')
    : null

  return (
    <>
      {isEvictions && (
        <Tile
          header={{
            title: 'Eviction Related Proceedings',
          }}
        >
          {isEvictionReportPending(evictions) ? (
            <PendingStatusCard
              message="Eviction records check is being processed"
            />
          ) : (
            <>
              {evictionsByState.map((stateEvictions, stateIndex) => (
                <Collapse
                  key={stateEvictions.state}
                  small
                  header={({ CollapseIcon, collapsed }) => (
                <Tile.Inner
                  borderBottom={
                    calcTileInnerBorderBottom(evictionsByState, stateIndex) ||
                    !collapsed
                  }
                >
                  <CriminalRecordsTitleContainer
                    data-testid={`housing-court-records-${stateEvictions.state}`}
                  >
                    <div>
                      <strong>State {stateEvictions.state},</strong>
                    </div>
                    <CriminalRecordsPeriod>
                      {stateEvictions.endDate
                        ? `${formatDate(
                            stateEvictions.startDate,
                          )} - ${formatDate(stateEvictions.endDate)}`
                        : `${formatDate(stateEvictions.startDate)}`}
                    </CriminalRecordsPeriod>
                    <CollapseIcon />
                  </CriminalRecordsTitleContainer>
                </Tile.Inner>
              )}
            >
              {stateEvictions.records.map((record, recordIndex) => {
                const recordKey = `${stateEvictions.state}-${recordIndex}`
                return (
                  <Tile.Inner
                    key={recordKey}
                    data-testid={`housing-court-record-${recordIndex}`}
                    inset
                    borderBottom={
                      stateIndex === evictionsByState.length - 1
                        ? calcTileInnerBorderBottom(
                            stateEvictions.records,
                            recordIndex,
                          )
                        : true
                    }
                  >
                    <Row type="flex" gutter={[24, 16]}>
                      <EvictionStyledCol xs={12} md={4} lg={3} xl={2}>
                        <EvictionStyledLabel>Date</EvictionStyledLabel>
                        {formatDate(record.date)}
                      </EvictionStyledCol>
                      <EvictionStyledCol xs={12} md={4} lg={3} xl={2}>
                        <EvictionStyledLabel>Record Source</EvictionStyledLabel>
                        {getValueOrNA(_.get(record, 'recordSource'))}
                      </EvictionStyledCol>
                      <EvictionStyledCol xs={12} md={4} lg={3} xl={2}>
                        <EvictionStyledLabel>File Number</EvictionStyledLabel>
                        {getValueOrNA(record.fileNumber)}
                      </EvictionStyledCol>
                      <EvictionStyledCol xs={12} md={4} lg={3} xl={2}>
                        <Spacer hideGteMd />
                        <EvictionStyledLabel>Case Type</EvictionStyledLabel>
                        {getValueOrNA(record.caseType)}
                      </EvictionStyledCol>
                      <EvictionStyledCol xs={12} md={4} lg={3} xl={2}>
                        <Spacer hideGteMd />
                        <EvictionStyledLabel>Judgement</EvictionStyledLabel>
                        {getValueOrNA(record.judgement)}
                      </EvictionStyledCol>
                      {stateEvictions.state === 'MA' && record.evictionActionType && (
                        <EvictionStyledCol xs={12} md={8} lg={4}>
                          <Spacer hideGteMd />
                          <EvictionStyledLabel>Eviction Action Type**</EvictionStyledLabel>
                          <span data-testid="housing-eviction-action-type">
                            {record.evictionActionType}
                          </span>
                        </EvictionStyledCol>
                      )}
                      <EvictionStyledCol xs={24} md={8} lg={6} xl={5}>
                        <Spacer hideGteMd />
                        <EvictionStyledLabel>Court Reported Amount*</EvictionStyledLabel>
                        {printDollarsFromCents(
                          record.judgement === 'SETTLED' && record.judgementSatAmount !== undefined
                            ? record.judgementSatAmount
                            : record.judgementAmount,
                        )}
                      </EvictionStyledCol>
                      <EvictionStyledCol xs={12} md={8} lg={4}>
                        <Spacer hideGteMd />
                        <EvictionStyledLabel>Release Date</EvictionStyledLabel>
                        {formatDate(_.get(record, 'releaseDate'))}
                      </EvictionStyledCol>
                      {isExperian && record.plaintiff && (
                        <EvictionStyledCol xs={12} md={8} lg={6}>
                          <Spacer hideGteMd />
                          <EvictionStyledLabel>Plaintiff</EvictionStyledLabel>
                          {record.plaintiff}
                        </EvictionStyledCol>
                      )}
                      {isExperian && record.judgementSatAmount !== undefined && (
                        <EvictionStyledCol xs={12} md={8} lg={6}>
                          <Spacer hideGteMd />
                          <EvictionStyledLabel>Settlement Amount</EvictionStyledLabel>
                          {printDollarsFromCents(record.judgementSatAmount)}
                        </EvictionStyledCol>
                      )}
                      {isExperian && (
                        <EvictionStyledCol xs={12} md={8} lg={6}>
                          <Spacer hideGteMd />
                          <EvictionStyledLabel>Judgement Date</EvictionStyledLabel>
                          {formatDate(record.judgementDate)}
                        </EvictionStyledCol>
                      )}
                      {isExperian && (
                        <EvictionStyledCol xs={12} md={8} lg={6}>
                          <Spacer hideGteMd />
                          <EvictionStyledLabel>Dismissal Date</EvictionStyledLabel>
                          {formatDate(record.dismissalDate)}
                        </EvictionStyledCol>
                      )}
                      {isExperian && record.defaultJudgement !== undefined && (
                        <EvictionStyledCol xs={12} md={8} lg={6}>
                          <Spacer hideGteMd />
                          <EvictionStyledLabel>Default Judgment</EvictionStyledLabel>
                          <span data-testid="eviction-default-judgment">
                            {record.defaultJudgement ? 'Yes' : 'No'}
                          </span>
                        </EvictionStyledCol>
                      )}
                    </Row>
                  </Tile.Inner>
                )
              })}
            </Collapse>
          ))}
          {evictionsByState.length > 0 && (
            <Tile.Inner borderBottom>
              <Row type="flex">
                <Col xs={24} md={24} lg={24} xl={24}>
                  <SmallText data-testid="housing-note-court-reported-amount">
                    *Court Reported Amount means the amount reported by the
                    court as a final judgment amount, or an amount reflecting
                    full or partial satisfaction of such judgment and may
                    include attorney or court fees or costs.
                  </SmallText>
                  {evictionActionTypeNote && (
                    <>
                      <br />
                      <SmallText data-testid="housing-note-eviction-action-type">
                        **{evictionActionTypeNote}
                      </SmallText>
                    </>
                  )}
                </Col>
              </Row>
            </Tile.Inner>
          )}
            </>
          )}
        </Tile>
      )}
    </>
  );
};

export default Evictions;
